import React, { useCallback, useEffect, useMemo, useState } from "react";
import { CalendarDays, ChevronLeft, ChevronRight, Landmark, MoreHorizontal, Plus, ReceiptText, Search, Tag, Trash2, WalletCards } from "lucide-react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { PageContainer } from "@/components/layout/PageContainer";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { useAuth, useWorkspace } from "@/lib/store";
import { useLocalization } from "@/lib/localization";
import { formatOperatingCurrency } from "@/lib/money";
import { createExpense, deleteExpense, fetchExpenses, updateExpense } from "@/lib/shopApi";
import { toast } from "sonner";
import { parityCopy } from "@/lib/parityLocalization";

const CATEGORIES = ["rent", "utilities", "transport", "wages", "supplies", "maintenance", "taxes", "marketing", "other"];
const METHODS = ["cash", "transfer", "card", "credit", "other"];
const neutralFocusClassName = "focus-visible:border-input focus-visible:!ring-0 focus-visible:!ring-offset-0";

function createExpenseSchema(copy) {
  return z.object({
    amount: z.string().trim().min(1, copy.invalid).refine((value) => {
      const amount = Number(value);
      return Number.isFinite(amount) && amount > 0;
    }, copy.invalid),
    category: z.enum(CATEGORIES, { required_error: copy.invalid, invalid_type_error: copy.invalid }),
    date: z.date({ required_error: copy.invalid, invalid_type_error: copy.invalid }).refine((value) => !Number.isNaN(value.getTime()), copy.invalid),
    description: z.string().trim().min(1, copy.invalid).max(160, copy.invalid),
    note: z.string().trim().max(300, copy.invalid),
    paymentMethod: z.enum(METHODS, { required_error: copy.invalid, invalid_type_error: copy.invalid }),
  });
}

function monthRange(month) {
  const start = new Date(month.getFullYear(), month.getMonth(), 1);
  const end = new Date(month.getFullYear(), month.getMonth() + 1, 1);
  return { from: start.toISOString(), to: end.toISOString() };
}

function dateValue(value = new Date()) {
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function createId() {
  return globalThis.crypto?.randomUUID?.() || `expense-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

const emptyForm = () => ({ amount: "", category: "supplies", date: new Date(), description: "", note: "", paymentMethod: "cash" });

export default function Expenses() {
  const { session } = useAuth();
  const { currentBranch } = useWorkspace();
  const { language, locale } = useLocalization();
  const copy = parityCopy[language].expenses;
  const [month, setMonth] = useState(() => new Date());
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [query, setQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const expenseSchema = useMemo(() => createExpenseSchema(copy), [copy]);
  const expenseForm = useForm({
    defaultValues: emptyForm(),
    resolver: zodResolver(expenseSchema),
  });

  const load = useCallback(async () => {
    if (!session?.sessionId) return;
    setLoading(true);
    try {
      const response = await fetchExpenses(session.sessionId, monthRange(month));
      setItems(response?.expenseList?.expenses ?? []);
      setTotal(Number(response?.expenseList?.total || 0));
    } catch (error) {
      toast.error(error?.message || copy.loadedError);
    } finally {
      setLoading(false);
    }
  }, [copy.loadedError, month, session?.sessionId]);

  useEffect(() => { void load(); }, [currentBranch?.id, load]);

  const visibleItems = useMemo(() => {
    const normalized = query.trim().toLocaleLowerCase(locale);
    return items.filter((item) => (
      (categoryFilter === "all" || item.category === categoryFilter) &&
      (!normalized || `${item.description} ${item.note || ""}`.toLocaleLowerCase(locale).includes(normalized))
    ));
  }, [categoryFilter, items, locale, query]);

  const categoryLeader = useMemo(() => {
    const totals = items.reduce((result, item) => ({ ...result, [item.category]: (result[item.category] || 0) + Number(item.amount || 0) }), {});
    return Object.entries(totals).sort((a, b) => b[1] - a[1])[0] || null;
  }, [items]);

  function openForm(item = null) {
    setEditing(item);
    expenseForm.reset(item ? {
      amount: String(item.amount), category: item.category, date: dateValue(item.occurredAtIso),
      description: item.description, note: item.note || "", paymentMethod: item.paymentMethod,
    } : emptyForm());
    setDrawerOpen(true);
  }

  async function save(values) {
    const amount = Number(values.amount);
    setSaving(true);
    const now = new Date().toISOString();
    const expense = {
      amount,
      category: values.category,
      createdAtIso: editing?.createdAtIso || now,
      description: values.description.trim(),
      id: editing?.id || createId(),
      note: values.note.trim(),
      occurredAtIso: new Date(values.date.getFullYear(), values.date.getMonth(), values.date.getDate(), 12).toISOString(),
      paymentMethod: values.paymentMethod,
      updatedAtIso: now,
    };
    try {
      if (editing) await updateExpense(session.sessionId, expense);
      else await createExpense(session.sessionId, expense);
      setDrawerOpen(false);
      toast.success(editing ? copy.updated : copy.added);
      await load();
    } catch (error) {
      toast.error(error?.message || copy.saveError);
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!deleteTarget) return;
    try {
      await deleteExpense(session.sessionId, deleteTarget.id);
      setDeleteTarget(null);
      setDrawerOpen(false);
      toast.success(copy.deleted);
      await load();
    } catch (error) {
      toast.error(error?.message || copy.deleteError);
    }
  }

  const monthLabel = month.toLocaleDateString(locale, { month: "long", year: "numeric" });

  return (
    <PageContainer>
      <div className="mx-auto max-w-6xl">
        <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-primary/15 bg-primary/[0.06] px-3 py-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-primary"><Landmark className="h-3.5 w-3.5" /> {copy.eyebrow}</div>
            <h1 className="mt-3 text-3xl font-semibold tracking-[-0.035em]">{copy.title}</h1>
            <p className="mt-1 text-sm text-muted-foreground">{copy.subtitle}</p>
          </div>
          <Button onClick={() => openForm()} className="shine-hover h-10 rounded-xl px-5 shadow-[0_10px_24px_-14px_hsl(var(--primary))]"><Plus className="mr-1.5 h-4 w-4" /> {copy.add}</Button>
        </header>
        <div className="mt-4 h-px bg-gradient-to-r from-border via-border to-transparent" />

        <section className="mt-5 grid gap-3 sm:grid-cols-3">
          <Metric icon={WalletCards} label={copy.thisMonth} value={formatOperatingCurrency(total, locale)} prominent />
          <Metric icon={ReceiptText} label={copy.entries} value={String(items.length)} />
          <Metric icon={Tag} label={copy.largestCategory} value={categoryLeader ? copy.categories[categoryLeader[0]] : "—"} meta={categoryLeader ? formatOperatingCurrency(categoryLeader[1], locale) : copy.noneYet} tone="warm" />
        </section>

        <section className="luxe-card mt-5 overflow-hidden rounded-[20px] border bg-card">
          <div className="flex flex-col gap-3 border-b border-border/70 bg-gradient-to-r from-muted/35 via-card to-card p-4 md:flex-row md:items-center">
            <div className="flex items-center gap-1 rounded-xl border border-border/70 bg-background/60 p-1 shadow-sm">
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setMonth((value) => new Date(value.getFullYear(), value.getMonth() - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
              <div className="flex min-w-36 items-center justify-center gap-2 text-center text-sm font-semibold"><CalendarDays className="h-3.5 w-3.5 text-primary" />{monthLabel}</div>
              <Button variant="ghost" size="icon" className="h-9 w-9" onClick={() => setMonth((value) => new Date(value.getFullYear(), value.getMonth() + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
            </div>
            <div className="relative min-w-0 flex-1"><Search className="absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" /><Input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={copy.search} className="h-11 rounded-xl border-border/70 bg-background/70 pl-10 shadow-sm" /></div>
            <Select value={categoryFilter} onValueChange={setCategoryFilter}><SelectTrigger className="h-11 w-full rounded-xl border-border/70 bg-background/70 shadow-sm md:w-44"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">{copy.allCategories}</SelectItem>{CATEGORIES.map((category) => <SelectItem key={category} value={category}>{copy.categories[category]}</SelectItem>)}</SelectContent></Select>
          </div>
          <div className="divide-y">
            {loading ? <Empty text={copy.loading} loading /> : visibleItems.length ? visibleItems.map((item) => (
              <button key={item.id} onClick={() => openForm(item)} className="group grid w-full grid-cols-[1fr_auto] items-center gap-4 px-5 py-4 text-left transition-colors hover:bg-primary/[0.035] md:grid-cols-[120px_1fr_150px_130px_auto]">
                <div className="hidden text-xs text-muted-foreground md:block">{new Date(item.occurredAtIso).toLocaleDateString(locale, { day: "numeric", month: "short" })}</div>
                <div className="min-w-0"><div className="truncate text-sm font-semibold">{item.description}</div><div className="mt-0.5 text-xs text-muted-foreground md:hidden">{copy.categories[item.category]} · {new Date(item.occurredAtIso).toLocaleDateString(locale, { day: "numeric", month: "short" })}</div></div>
                <div className="hidden md:block"><span className="inline-flex rounded-full border border-primary/10 bg-primary/[0.055] px-2.5 py-1 text-[10px] font-semibold text-primary">{copy.categories[item.category]}</span></div>
                <div className="hidden text-xs text-muted-foreground md:block">{copy.methods[item.paymentMethod]}</div>
                <div className="flex items-center gap-2 font-mono text-sm font-semibold tabular-nums">{formatOperatingCurrency(item.amount, locale)}<span className="grid h-7 w-7 place-items-center rounded-full text-muted-foreground transition-colors group-hover:bg-primary/10 group-hover:text-primary"><MoreHorizontal className="h-4 w-4" /></span></div>
              </button>
            )) : <Empty text={copy.empty} onAdd={!query && categoryFilter === "all" ? () => openForm() : null} addLabel={copy.add} />}
          </div>
        </section>
      </div>

      <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
        <SheetContent className="flex w-full flex-col overflow-y-auto sm:max-w-md">
          <SheetHeader><SheetTitle>{editing ? copy.edit : copy.add}</SheetTitle><SheetDescription>{copy.impact}</SheetDescription></SheetHeader>
          <Form {...expenseForm}>
            <form onSubmit={expenseForm.handleSubmit(save)} className="flex flex-1 flex-col" noValidate>
              <div className="flex-1 space-y-5 py-6">
                <FormField control={expenseForm.control} name="description" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">{copy.description}</FormLabel><FormControl><Input {...field} autoFocus placeholder={copy.example} className={neutralFocusClassName} /></FormControl><FormMessage /></FormItem>} />
                <div className="grid grid-cols-2 gap-3">
                  <FormField control={expenseForm.control} name="amount" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">{copy.amount}</FormLabel><FormControl><Input {...field} inputMode="decimal" placeholder="0" className={neutralFocusClassName} /></FormControl><FormMessage /></FormItem>} />
                  <FormField control={expenseForm.control} name="date" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">{copy.date}</FormLabel><Popover><PopoverTrigger asChild><FormControl><Button type="button" variant="outline" className={`w-full justify-between px-3 font-normal ${neutralFocusClassName}`}><span className="truncate">{field.value ? field.value.toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : copy.date}</span><CalendarDays className="h-4 w-4 shrink-0 text-muted-foreground" /></Button></FormControl></PopoverTrigger><PopoverContent className="w-auto p-0" align="end"><Calendar mode="single" selected={field.value} onSelect={field.onChange} initialFocus /></PopoverContent></Popover><FormMessage /></FormItem>} />
                </div>
                <FormField control={expenseForm.control} name="category" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">{copy.category}</FormLabel><Select value={field.value} onValueChange={field.onChange}><FormControl><SelectTrigger className={neutralFocusClassName}><SelectValue /></SelectTrigger></FormControl><SelectContent>{CATEGORIES.map((category) => <SelectItem key={category} value={category}>{copy.categories[category]}</SelectItem>)}</SelectContent></Select><FormMessage /></FormItem>} />
                <FormField control={expenseForm.control} name="paymentMethod" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">{copy.payment}</FormLabel><Select value={field.value} onValueChange={field.onChange}><FormControl><SelectTrigger className={neutralFocusClassName}><SelectValue /></SelectTrigger></FormControl><SelectContent>{METHODS.map((method) => <SelectItem key={method} value={method}>{copy.methods[method]}</SelectItem>)}</SelectContent></Select><FormMessage /></FormItem>} />
                <FormField control={expenseForm.control} name="note" render={({ field }) => <FormItem><FormLabel className="text-xs text-muted-foreground">{copy.note}</FormLabel><FormControl><Input {...field} placeholder={copy.notePlaceholder} className={neutralFocusClassName} /></FormControl><FormMessage /></FormItem>} />
                {editing ? <Button type="button" variant="ghost" className={`text-destructive hover:text-destructive ${neutralFocusClassName}`} onClick={() => setDeleteTarget(editing)}><Trash2 className="mr-2 h-4 w-4" /> {copy.delete}</Button> : null}
              </div>
              <SheetFooter><Button type="button" variant="outline" className={neutralFocusClassName} onClick={() => setDrawerOpen(false)}>{copy.cancel}</Button><Button type="submit" className={neutralFocusClassName} disabled={saving}>{saving ? copy.saving : copy.save}</Button></SheetFooter>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => !open && setDeleteTarget(null)}><AlertDialogContent><AlertDialogHeader><AlertDialogTitle>{copy.deleteTitle}</AlertDialogTitle><AlertDialogDescription>{copy.deleteBody}</AlertDialogDescription></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>{copy.keep}</AlertDialogCancel><AlertDialogAction onClick={remove} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">{copy.delete}</AlertDialogAction></AlertDialogFooter></AlertDialogContent></AlertDialog>
    </PageContainer>
  );
}

function Metric({ icon: Icon, label, value, meta, prominent = false, tone = "default" }) {
  const surface = prominent
    ? "border-emerald-800/40 bg-[linear-gradient(135deg,#063f38_0%,#0a5b4e_58%,#087060_100%)] text-white shadow-[0_18px_45px_-28px_rgba(6,78,65,0.9)]"
    : tone === "warm"
      ? "border-warning/20 bg-gradient-to-br from-card via-card to-warning/[0.07]"
      : "border-primary/15 bg-gradient-to-br from-card via-card to-primary/[0.055]";
  return <div className={`luxe-card bg-grain min-h-[126px] overflow-hidden rounded-[20px] border p-4 ${surface}`}>
    <div className={`absolute -right-8 -top-10 h-28 w-28 rounded-full blur-3xl ${prominent ? "bg-emerald-200/20" : tone === "warm" ? "bg-warning/10" : "bg-primary/10"}`} />
    <div className="flex items-center justify-between gap-3">
      <div className={`text-[9px] font-semibold uppercase tracking-[0.19em] ${prominent ? "text-emerald-50/75" : "text-muted-foreground"}`}>{label}</div>
      <span className={`grid h-8 w-8 place-items-center rounded-xl ring-1 ring-inset ${prominent ? "bg-white/10 text-emerald-50 ring-white/15" : tone === "warm" ? "bg-warning/10 text-warning ring-warning/15" : "bg-primary/10 text-primary ring-primary/15"}`}><Icon className="h-4 w-4" /></span>
    </div>
    <div className={`numeral mt-3 truncate text-[23px] font-semibold capitalize leading-none tracking-[-0.035em] ${prominent ? "text-white" : "text-foreground"}`}>{value}</div>
    <div className={`mt-2 text-[11px] ${prominent ? "text-emerald-50/70" : "text-muted-foreground"}`}>{meta || <span className="inline-block h-1.5 w-1.5 rounded-full bg-current opacity-50" />}</div>
  </div>;
}
function Empty({ text, onAdd, addLabel, loading = false }) { return <div className="relative overflow-hidden px-5 py-16 text-center">
  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_center,hsl(var(--primary)/0.055),transparent_45%)]" />
  <div className={`relative mx-auto grid h-12 w-12 place-items-center rounded-2xl border border-primary/15 bg-primary/[0.06] text-primary shadow-sm ${loading ? "animate-pulse" : ""}`}><ReceiptText className="h-5 w-5" /></div>
  <div className="relative mt-3 text-sm font-medium text-muted-foreground">{text}</div>
  {onAdd ? <Button variant="outline" className="relative mt-4 h-9 rounded-xl bg-card" onClick={onAdd}><Plus className="mr-1.5 h-3.5 w-3.5" />{addLabel}</Button> : null}
</div>; }
